/**
 * Environment Variable Validation
 * 
 * Validates that required VITE_* environment variables are set.
 * Fails fast at app startup with a clear error message instead of
 * letting missing config surface as cryptic SDK errors.
 */

/**
 * Assert that all specified environment variables are present and non-empty.
 * 
 * @param {string} service - Service name for error messages (e.g. 'Firebase')
 * @param {string[]} keys - Array of VITE_* variable names to check
 * @throws {Error} If any required variable is missing or empty
 */
export function requireEnv(service, keys) {
  const missing = keys.filter(key => !import.meta.env[key]);
  if (missing.length > 0) {
    throw new Error(
      `[${service}] Missing required environment variables:\n` +
      missing.map(k => `  - ${k}`).join('\n') + '\n' +
      `Check your .env.${import.meta.env.MODE} file. See .env.example for reference.`
    );
  }
}

/**
 * Validate talent field values against agency field definitions.
 *
 * Enforces isRequired on custom fields, respecting conditions and isEnabled.
 * Always enforces firstName (system field). Never enforces isRequired on lastName.
 *
 * @param {Object} talent  - Flat talent record (field values keyed by fieldId)
 * @param {Object} fields  - Agency talentFields (field definitions keyed by fieldId)
 * @returns {{ valid: boolean, errors: Object.<string, string> }}
 */
export function validateTalentFields(talent, fields) {
  const errors = {};

  for (const [fieldId, fieldDef] of Object.entries(fields)) {
    // Skip disabled fields — they are not shown and not validated
    if (fieldDef.isEnabled === false) continue;

    // Skip when condition is unmet — condition only gates input visibility
    if (fieldDef.condition) {
      const { field: condField, value: condValue } = fieldDef.condition;
      if (talent[condField] !== condValue) continue;
    }

    if (!fieldDef.isRequired) continue;

    const value = talent[fieldId];
    const isEmpty =
      value === undefined ||
      value === null ||
      value === '' ||
      (Array.isArray(value) && value.length === 0);

    if (isEmpty) {
      errors[fieldId] = `${fieldDef.name} is required.`;
    }
  }

  // System field: firstName is always required
  if (!talent.firstName || String(talent.firstName).trim() === '') {
    errors.firstName = 'First name is required.';
  }

  // Authoritative rule: lastName is never required regardless of agency config
  delete errors.lastName;

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}
